// Attach a recording to a share link: the only path that persists an attempt,
// and only on explicit opt-in. The Worker returns a delete token once (stores
// its hash); we keep it in localStorage so the user can revoke without an account.

export const SHARE_VOICE_KEY = 'ezshadowSharedVoice.v1'

// Matches the Worker's SHARE_MAX_BYTES; a 30s attempt is far under it.
export const MAX_SHARE_BYTES = 512 * 1024

export const UPLOAD_TIMEOUT_MS = 15000

// Mirrors the Worker's allowlist.
const ALLOWED_TYPES = ['audio/webm', 'audio/mp4', 'audio/ogg', 'audio/mpeg', 'audio/wav']

export function ShareVoiceError (code, message) {
  const err = new Error(message || code)
  err.name = 'ShareVoiceError'
  err.code = code // 'TOO_LARGE' | 'UNSUPPORTED_TYPE' | 'RATE_LIMITED' | 'TIMEOUT' | 'NETWORK' | 'UNAVAILABLE'
  return err
}

export function isConfigured () {
  return !!process.env.TRANSCRIPT_API_URL
}

export function baseMimeType (raw) {
  const type = String(raw || '').split(';')[0].trim().toLowerCase()
  return ALLOWED_TYPES.indexOf(type) === -1 ? null : type
}

export async function uploadAttempt (blob) {
  if (!blob || !blob.size) throw ShareVoiceError('NETWORK', 'no recording to upload')
  if (blob.size > MAX_SHARE_BYTES) {
    throw ShareVoiceError('TOO_LARGE', 'recording is ' + blob.size + ' bytes')
  }

  const type = baseMimeType(blob.type)
  if (!type) throw ShareVoiceError('UNSUPPORTED_TYPE', 'cannot share ' + blob.type)

  const apiUrl = process.env.TRANSCRIPT_API_URL
  if (!apiUrl) throw ShareVoiceError('UNAVAILABLE', 'TRANSCRIPT_API_URL is not configured')

  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  const timer = setTimeout(function () {
    if (controller) controller.abort()
  }, UPLOAD_TIMEOUT_MS)

  let response
  try {
    response = await fetch(apiUrl + '/v1/share/audio', {
      method: 'POST',
      headers: { 'Content-Type': type },
      body: blob,
      signal: controller ? controller.signal : undefined
    })
  } catch (e) {
    clearTimeout(timer)
    const aborted = e && (e.name === 'AbortError' || e.code === 20)
    throw ShareVoiceError(aborted ? 'TIMEOUT' : 'NETWORK', e && e.message)
  }
  clearTimeout(timer)

  if (!response.ok) {
    if (response.status === 429) throw ShareVoiceError('RATE_LIMITED', 'too many uploads, try again shortly')
    if (response.status === 413) throw ShareVoiceError('TOO_LARGE', 'recording rejected as too large')
    if (response.status === 415) throw ShareVoiceError('UNSUPPORTED_TYPE', 'recording format rejected')
    throw ShareVoiceError('UNAVAILABLE', 'share API responded ' + response.status)
  }

  let body
  try {
    body = await response.json()
  } catch (e) {
    throw ShareVoiceError('UNAVAILABLE', 'share API returned a non-JSON body')
  }

  if (!body || !body.id || !body.deleteToken) {
    throw ShareVoiceError('UNAVAILABLE', 'share API returned an incomplete response')
  }
  return { id: body.id, deleteToken: body.deleteToken }
}

// Caller revokes the URL. fetch(), not <audio src>: media loads send no Origin
// and the Worker 403s them. playSelf carries the iOS routing handling.
export async function fetchSharedAudioUrl (id) {
  const apiUrl = process.env.TRANSCRIPT_API_URL
  if (!apiUrl) throw ShareVoiceError('UNAVAILABLE', 'TRANSCRIPT_API_URL is not configured')

  let response
  try {
    response = await fetch(apiUrl + '/v1/share/audio?id=' + encodeURIComponent(id))
  } catch (e) {
    throw ShareVoiceError('NETWORK', e && e.message)
  }
  if (!response.ok) {
    throw ShareVoiceError(response.status === 404 ? 'NOT_FOUND' : 'UNAVAILABLE',
      'share audio responded ' + response.status)
  }
  const blob = await response.blob()
  return URL.createObjectURL(blob)
}

export async function deleteShared (id, deleteToken) {
  const apiUrl = process.env.TRANSCRIPT_API_URL
  if (!apiUrl) return false

  let response
  try {
    response = await fetch(apiUrl + '/v1/share/audio?id=' + encodeURIComponent(id), {
      method: 'DELETE',
      headers: { 'X-Delete-Token': deleteToken }
    })
  } catch (e) {
    return false
  }
  const gone = response.ok || response.status === 404
  if (gone) forgetShared(id)
  return gone
}

// The only record of what was shared, so it is written on upload, not on share.

export function listShared () {
  try {
    const parsed = JSON.parse(localStorage.getItem(SHARE_VOICE_KEY))
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    return []
  }
}

export function rememberShared (entry) {
  if (!entry || !entry.id) return
  const list = listShared().filter(function (e) { return e.id !== entry.id })
  list.push({
    id: entry.id,
    deleteToken: entry.deleteToken,
    createdAt: entry.createdAt || new Date().toISOString(),
    sentence: entry.sentence || ''
  })
  save(list)
}

export function forgetShared (id) {
  save(listShared().filter(function (e) { return e.id !== id }))
}

export async function deleteAllShared () {
  const list = listShared()
  let removed = 0
  for (let i = 0; i < list.length; i++) {
    /* eslint-disable no-await-in-loop */
    const ok = await deleteShared(list[i].id, list[i].deleteToken)
    /* eslint-enable no-await-in-loop */
    if (ok) removed++
  }
  return removed
}

function save (list) {
  try {
    localStorage.setItem(SHARE_VOICE_KEY, JSON.stringify(list))
  } catch (e) { /* storage unavailable or full */ }
}
